import React, { useContext, useEffect } from 'react';
import { useNavigate } from "react-router-dom";

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { colorAuriaYellow } from 'src/GlobalStyles/MuiTheme';

import { Roles } from 'src/Constants/BorgEnums';
import { handleLogout } from 'src/Components/LogoutUtils';
import { LoggedInUserContext } from '../../Components/Admin/AdminPageContainer'; // Import context

import borgLogo from "../../assets/img/Borg-logo.png";
import kepleraLogo from "../../assets/img/Keplera-logo.png";
import Cookies from 'universal-cookie';

const isKepleraVersion = process.env.REACT_APP_PRODUCT_NAME === "keplera";
const productLogo = isKepleraVersion ? kepleraLogo : borgLogo;

export const APP_BAR_HEIGHT_PX = 140; // rough estimate
const BUTTON_PADDING = 2;
const colorBorgBlue = "#5BF8FF";

type Props = {
  onSelectedPageChange: (page: string) => void;
};

const AdminAppBar = ({ onSelectedPageChange }: Props) => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);

  const loggedInUser = useContext(LoggedInUserContext)?.loggedInUser;
  const selectedPage = useContext(LoggedInUserContext)?.selectedPage;   // Selected page (used for highlighting the selected page)
  const setQuizContext = useContext(LoggedInUserContext)?.setQuizContext;

  const navigate = useNavigate();

  // Update useEffect to handle URL-based page selection
  useEffect(() => {
    const path = window.location.pathname;
    const section = path.split('/')[1]; // Get the first part of the path

    if (section === 'instructor') {
      onSelectedPageChange("instructor");
    } else if (loggedInUser?.roles.includes(Roles.OrganizationAdmin) || loggedInUser?.roles.includes(Roles.SystemAdmin)) {
      onSelectedPageChange("admin");
    }
  }, [loggedInUser, window.location.pathname]); // Update the selected page when the URL changes or page is refreshed
  const handleOpenNavMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(e.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleClickNavButton = (e: React.MouseEvent<HTMLElement>, url: string = "") => {
    setAnchorElNav(null);

    // clear the quiz context for instructor gradebook page when navigating to a new page
    // This is to prevent the quiz context from persisting when navigating away from the page
    if (setQuizContext) {
      setQuizContext('', '');
    } else {
      console.error("setQuizContext is undefined");
    }

    //Parse out the root path from the URL
    const page = url.split("/")[1];
    onSelectedPageChange(page); // This updates selectedPage in context

    if (url) {
      navigate(url);
    }
  };

  const handlePlayClick = () => {
    window.open("/play", "_blank", "noreferrer");
  }

  const cookies = new Cookies();

  //Conditionally render the Admin page based on role and build type 
  const isAdmin = (loggedInUser?.roles?.includes(Roles.OrganizationAdmin) || loggedInUser?.roles?.includes(Roles.SystemAdmin)) ?? false;
  const isInstructor = loggedInUser?.roles?.includes(Roles.Instructor) ?? false;

  const pages = [
    { key: "admin", title: 'Admin', url: "/admin/users" },
    { key: "instructor", title: 'Instructor', url: "/instructor" },
  ];
  let filteredPages = pages;

  //Page filtering
  if (isInstructor) {
    //Remove all pages Instructor doesn't have permissions to
    filteredPages = filteredPages.filter(page => page.title === 'Instructor');
  } else if (isAdmin) {
    //Remove all pages SystemAdmin doesn't have permissions to;
  }

  return (
    <AppBar position="sticky">
      <Container maxWidth={false}>
        <Toolbar disableGutters>

          <Box sx={{ flexGrow: 0.5, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {filteredPages.map((page) => (
                <MenuItem key={page.key} onClick={(e) => handleClickNavButton(e, page.url)}>
                  <Typography textAlign="center" sx={{ color: selectedPage === page.key ? colorAuriaYellow : '' }}>
                    {page.title}
                  </Typography>
                </MenuItem>
              ))}
              <MenuItem>
                <Button
                  sx={{
                    display: 'block',
                    backgroundColor: colorBorgBlue,
                    borderRadius: '30px',
                    color: 'black',
                  }}
                  onClick={handlePlayClick}
                >
                  Play
                  {selectedPage}
                </Button>

              </MenuItem>
            </Menu>
          </Box>

          <div className="borg-logo-appbar">
            <img alt="logo" src={productLogo} />
          </div>

          { /* Full size nav bar */}
          <Box sx={{ flexGrow: 1, gap: 1, display: { xs: 'none', md: 'flex' }, paddingLeft: BUTTON_PADDING }}>
            { /* TODO - Use Tabs */}
            {filteredPages.map((page) => (
              <Link key={page.key} onClick={(e) => handleClickNavButton(e, page.url)} sx={{ textDecoration: 'none' }}>
                <Button
                  key={page.key}
                  sx={{ my: 2, display: 'block', color: selectedPage === page.key ? colorAuriaYellow : '' }}
                >
                  {page.title}
                </Button>
              </Link>
            ))}

            <Button
              sx={{
                my: 2, display: 'block',
                backgroundColor: colorBorgBlue,
                borderRadius: '30px',
                color: 'black',
                paddingTop: '8px', // Add padding to help with the text offset
              }}
              onClick={handlePlayClick}
            >
              Play
            </Button>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
            Logged in as: {cookies.get("username")}
            <Tooltip title="Logout">
              <IconButton
                sx={{ marginLeft: 'auto' }}
                size="large"
                aria-label="=logout"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={() => handleLogout(navigate)}
                color="inherit"
              >
                <ExitToAppIcon />
              </IconButton>
            </Tooltip>
          </Box>

        </Toolbar>
      </Container>
    </AppBar>
  );
}
export default AdminAppBar;